import {
    DefaultOptions,
    QueryClient,
    QueryClientConfig,
    QueryFunction,
} from '@tanstack/react-query'

import { AuthorizedFetch, createAuthorizedFetch } from '../react/useAuthorizedFetch'
import { AuthRuntime } from '../runtime/AuthRuntime'

/**
 * Treat any error whose `status === 401` as already-handled by the
 * `authorizedFetch` retry pipeline — do not retry inside React Query.
 * Anything else falls back to the consumer's retry policy (default: 3 tries).
 */
const isAlreadyHandledAuthError = (err: unknown): boolean => {
    const status = (err as { status?: number } | null)?.status
    return status === 401 || status === 403
}

export interface AuthRetryPolicyOptions {
    /** Max query retries for non-auth errors. Default: 3. */
    maxQueryRetries?: number
    /** Whether to retry mutations on non-auth errors. Default: false. */
    retryMutations?: boolean
}

/**
 * Compose default options that:
 * 1. Never retry queries/mutations on 401/403 — `authorizedFetch` already
 *    ran a single-flight refresh + one retry. A second retry inside RQ
 *    would consume the refresh token a second time and trip Keycloak's
 *    "Maximum allowed refresh token reuse exceeded".
 * 2. Use the consumer's normal retry budget for everything else.
 */
export const authQueryDefaults = (opts: AuthRetryPolicyOptions = {}): DefaultOptions => {
    const maxQueryRetries = opts.maxQueryRetries ?? 3
    const retryMutations = opts.retryMutations ?? false
    return {
        queries: {
            retry: (failureCount, error) => {
                if (isAlreadyHandledAuthError(error)) {
                    return false
                }
                return failureCount < maxQueryRetries
            },
        },
        mutations: {
            retry: retryMutations,
        },
    }
}

export interface CreateAuthorizedQueryClientOptions extends AuthRetryPolicyOptions {
    runtime: AuthRuntime
    /** Override the underlying fetch. Default: `globalThis.fetch`. */
    fetchImpl?: typeof fetch
    /** Extra config layered on top of the auth-aware defaults. */
    config?: QueryClientConfig
}

export interface AuthorizedQueryClient {
    queryClient: QueryClient
    /** Fetch wrapper bound to the runtime — attaches token, handles 401 retry. */
    fetch: AuthorizedFetch
}

/**
 * Build a fresh {@link QueryClient} wired up with auth-aware defaults plus
 * a matching {@link AuthorizedFetch} for consumers' queryFn / mutationFn.
 *
 * Usage:
 *
 * ```tsx
 * const { queryClient, fetch: authedFetch } = createAuthorizedQueryClient({ runtime })
 *
 * const useFlights = () => useQuery({
 *   queryKey: ['flights'],
 *   queryFn: () => authedFetch('/api/flights').then(r => r.json()),
 * })
 * ```
 */
export const createAuthorizedQueryClient = (
    options: CreateAuthorizedQueryClientOptions,
): AuthorizedQueryClient => {
    const authedFetch = createAuthorizedFetch(
        options.runtime,
        options.fetchImpl ?? globalThis.fetch,
    )
    const queryClient = new QueryClient({
        ...options.config,
        defaultOptions: {
            ...authQueryDefaults({
                maxQueryRetries: options.maxQueryRetries,
                retryMutations: options.retryMutations,
            }),
            ...options.config?.defaultOptions,
            queries: {
                ...authQueryDefaults({
                    maxQueryRetries: options.maxQueryRetries,
                }).queries,
                ...options.config?.defaultOptions?.queries,
            },
            mutations: {
                ...authQueryDefaults({
                    retryMutations: options.retryMutations,
                }).mutations,
                ...options.config?.defaultOptions?.mutations,
            },
        },
    })
    return { queryClient, fetch: authedFetch }
}

/**
 * Patch an existing {@link QueryClient} with the auth-aware retry policy.
 * Use this when the host already owns a QueryClient (e.g. via aos-singleton)
 * and you don't want to swap it out.
 *
 * The auth-aware `retry` field always wins, overwriting whatever the
 * consumer set before — a previous `retry: 5` would otherwise let RQ
 * re-fire 401-ed queries, consuming the refresh token a second time and
 * tripping Keycloak's "Maximum allowed refresh token reuse exceeded".
 * Every other field on `queries` / `mutations` (e.g. `staleTime`,
 * `gcTime`, `refetchOnWindowFocus`) is preserved from the previous
 * defaults.
 *
 * Returns the same client (for chaining).
 */
export const installAuthRetryPolicy = (
    queryClient: QueryClient,
    opts: AuthRetryPolicyOptions = {},
): QueryClient => {
    const previous = queryClient.getDefaultOptions()
    const defaults = authQueryDefaults(opts)
    queryClient.setDefaultOptions({
        ...previous,
        queries: {
            ...previous.queries,
            // Auth-aware retry must win — consumer values are otherwise preserved above.
            retry: defaults.queries?.retry,
        },
        mutations: {
            ...previous.mutations,
            retry: defaults.mutations?.retry,
        },
    })
    return queryClient
}

/**
 * Build a queryFn that calls `authorizedFetch` and parses JSON. Convenience
 * for the common case; consumers needing custom parsing should compose
 * `authorizedFetch` themselves.
 */
export const createAuthorizedJsonQueryFn =
    (authorizedFetch: AuthorizedFetch): QueryFunction =>
    async ({ queryKey, signal }) => {
        const url = String(queryKey[0])
        const response = await authorizedFetch(url, { signal })
        if (!response.ok) {
            const error = new Error(
                `Request to ${url} failed with status ${response.status}`,
            ) as Error & { status: number }
            error.status = response.status
            throw error
        }
        return response.json()
    }
